import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { downloadFile } from "@/lib/training";
import { formatDate } from "@/lib/constants";
import { RoleBadge, EmptyState } from "@/components/Badges";
import { StatTile, TrainingItemRow, TrainingStatus } from "@/components/training/TrainingBits";

export default function PersonRecord() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/reports/users/${id}`).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  }, [id]);

  if (error) return <EmptyState title="Record unavailable" description={error} testId="person-record-error" />;
  if (!data) return null;
  const { user, summary } = data;

  return (
    <div className="fade-up">
      <Link to="/reports" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-lasso-ink" data-testid="person-back-link"><ArrowLeft className="h-4 w-4" /> Training progress</Link>
      <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="person-name">{user.name}</h1>
          <p className="mt-2 flex items-center gap-3 text-sm text-muted-foreground"><RoleBadge role={user.role} /> {user.community_name || user.region_name || "Company-wide"} · {user.email}</p>
        </div>
      </div>
      <div className="my-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Completion" value={`${summary.pct}%`} testId="person-stat-completion" />
        <StatTile label="Assigned" value={summary.assigned} testId="person-stat-assigned" />
        <StatTile label="Overdue" value={summary.overdue} tone="danger" testId="person-stat-overdue" />
        <StatTile label="Certificates" value={data.certificates.length} testId="person-stat-certificates" />
      </div>

      <section className="mb-10">
        <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">Assigned training</h2>
        <div className="space-y-3">
          {data.items.length === 0 && <EmptyState title="No assigned training" />}
          {data.items.map((i) => <TrainingItemRow key={`${i.item_type}-${i.item_id}`} item={i} link={false} />)}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">Course activity</h2>
        <div className="overflow-hidden rounded-2xl border border-lasso-line bg-white">
          <Table data-testid="person-course-table">
            <TableHeader><TableRow><TableHead>Course</TableHead><TableHead>Status</TableHead><TableHead>Progress</TableHead><TableHead>Quiz attempts</TableHead><TableHead>Best / last</TableHead><TableHead>Certificate</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.progress.map((p) => (
                <TableRow key={p.course_id}>
                  <TableCell className="font-semibold">{p.title}</TableCell>
                  <TableCell><TrainingStatus status={p.status} /></TableCell>
                  <TableCell className="text-sm">{p.pct}%</TableCell>
                  <TableCell className="text-sm">{p.attempts}</TableCell>
                  <TableCell className="text-sm">{p.best_score != null ? `${p.best_score}% / ${p.last_score}%` : "—"}</TableCell>
                  <TableCell>
                    {p.certificate_code ? (
                      <button onClick={() => downloadFile(`/certificates/${p.certificate_code}/pdf`, `Lasso-Hub-${p.certificate_code}.pdf`)} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" data-testid={`person-cert-${p.certificate_code}`}>
                        <Download className="h-3.5 w-3.5" /> {p.certificate_code}
                      </button>
                    ) : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {data.progress.length === 0 && <TableRow><TableCell colSpan={6} className="text-sm text-muted-foreground">No course activity yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </section>

      {data.attempts.length > 0 && (
        <section>
          <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">Quiz attempt history</h2>
          <div className="divide-y divide-lasso-line rounded-2xl border border-lasso-line bg-white" data-testid="person-attempts">
            {data.attempts.map((a) => (
              <div key={a.id} className="flex justify-between px-5 py-3 text-sm">
                <span>{a.course_title} · {formatDate(a.created_at)}</span>
                <span className={`font-semibold ${a.passed ? "text-emerald-700" : "text-rose-600"}`}>{a.score}%</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
